import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { usePoints } from '../../context/PointsContext';
import BonusService, { ClaimableBonus } from '../../services/bonusService';
import BasePageLayout from '../../components/BasePageLayout';
import '../../styles/pages/ClaimBonusesPage.css';
import '../../styles/layout/wide-screen-layouts.css';

const ClaimBonusesPage: React.FC = () => {
  const [bonuses, setBonuses] = useState<ClaimableBonus[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [claimedIds, setClaimedIds] = useState<string[]>([]);
  const { user } = useAuth();
  const { t } = useLanguage();
  const { refreshPoints } = usePoints();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchClaimableBonuses = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Use centralized bonus service with caching
        const bonusService = BonusService.getInstance();
        const data = await bonusService.getClaimableBonuses(user.id);
        
        setBonuses(data);
      } catch (err) {
        console.error('Error fetching claimable bonuses:', err);
        setError('Failed to load your bonuses. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchClaimableBonuses();
  }, [user, navigate]);

  const handleClaimBonus = async (bonusId: string) => {
    if (!user) return;
    
    try {
      setClaimingId(bonusId);
      
      const { data, error } = await supabase.rpc('claim_bonus', { p_bonus_id: bonusId });
      
      if (error) throw error;
      
      if (data) {
        // Add to claimed list
        setClaimedIds(prev => [...prev, bonusId]);
        
        // Refresh points in the UI using the context
        await refreshPoints();
        
        // Notify bonus service to invalidate cache and update subscribers (TopNavbar)
        const bonusService = BonusService.getInstance();
        await bonusService.onBonusClaimed(user.id);
        
        // Remove from the list
        setBonuses(prev => prev.filter(bonus => bonus.id !== bonusId));
      }
    } catch (err) {
      console.error('Error claiming bonus:', err);
      setError('Failed to claim bonus. Please try again.');
    } finally {
      setClaimingId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('claimBonuses.neverExpires');
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getBonusIcon = (bonusType: string) => {
    switch (bonusType) {
      case 'welcome':
        return '👋';
      case 'daily':
        return '📅';
      case 'referral':
        return '👥';
      default:
        return '🎁';
    }
  };

  const getBonusTypeLabel = (bonusType: string) => {
    switch (bonusType) {
      case 'welcome':
        return t('claimBonuses.welcomeBonus');
      case 'daily':
        return t('claimBonuses.dailyLogin');
      case 'referral':
        return t('claimBonuses.referralBonus');
      default:
        return t('claimBonuses.specialBonus');
    }
  };

  const getBonusesAnalytics = () => {
    if (bonuses.length === 0) return null;
    
    const totalPoints = bonuses.reduce((sum, bonus) => sum + bonus.points, 0);
    const bonusByType = bonuses.reduce((acc, bonus) => {
      acc[bonus.bonus_type] = (acc[bonus.bonus_type] || 0) + bonus.points;
      return acc;
    }, {} as Record<string, number>);
    
    const expiringBonus = bonuses
      .filter(bonus => bonus.expires_at)
      .sort((a, b) => new Date(a.expires_at!).getTime() - new Date(b.expires_at!).getTime())[0];
    
    return {
      totalPoints,
      bonusByType,
      expiringBonus,
      count: bonuses.length
    };
  };

  const analytics = getBonusesAnalytics();

  return (
    <BasePageLayout maxWidth="none">
      <div className="claim-bonuses-page">
        {/* Header Section - Similar to ProfilePage */}
        <section className="bonuses-header-section">
          <div className="star-layer-1"></div>
          <div className="star-layer-2"></div>
          <div className="star-layer-3"></div>
          <div className="bonuses-header-content">
            <div className="bonuses-hero-centered">
              <div className="bonuses-icon-wrapper">
                <div className="bonuses-icon-large">
                  <span className="bonuses-icon-placeholder">💎</span>
                </div>
              </div>
              
              <div className="bonuses-info">
                <div className="bonuses-title-group">
                  <h1 className="bonuses-title-main">{t('claimBonuses.title')}</h1>
                  <div className="bonuses-badge">
                    <i className="bi bi-gift"></i>
                    {t('claimBonuses.availableRewards')}
                  </div>
                </div>
                <p className="bonuses-description">
                  {t('claimBonuses.description')}
                </p>
                
                {analytics && (
                  <div className="bonuses-meta">
                    <div className="meta-item">
                      <i className="bi bi-trophy"></i>
                      <span>{t('claimBonuses.totalPointsCount', { count: analytics.totalPoints })}</span>
                    </div>
                    <div className="meta-item">
                      <i className="bi bi-gift"></i>
                      <span>{t('claimBonuses.availableBonusesCount', { count: bonuses.length })}</span>
                    </div>
                    {analytics.expiringBonus && (
                      <div className="meta-item urgent">
                        <i className="bi bi-clock"></i>
                        <span>{t('claimBonuses.expiresDate', { date: formatDate(analytics.expiringBonus.expires_at) })}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Main 3-Column Grid Layout */}
        <section className="bonuses-wide-grid">
          {/* Left Column - Bonuses Overview */}
          <div className="bonuses-panel-left">
            <div className="overview-card">
              <h3 className="section-title">
                <i className="bi bi-pie-chart"></i>
                {t('claimBonuses.bonusesOverview')}
              </h3>
              
              <div className="bonus-stats-row">
                <div className="stat-card-compact">
                  <div className="stat-icon blue">
                    <i className="bi bi-gift"></i>
                  </div>
                  <div className="stat-content">
                    <div className="stat-number">{bonuses.length}</div>
                    <div className="stat-label">{t('claimBonuses.available').toUpperCase()}</div>
                  </div>
                </div>
                
                {analytics && (
                  <div className="stat-card-compact">
                    <div className="stat-icon green">
                      <i className="bi bi-trophy"></i>
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">{analytics.totalPoints}</div>
                      <div className="stat-label">{t('claimBonuses.pointsLabel').toUpperCase()}</div>
                    </div>
                  </div>
                )}
              </div>

              {analytics && analytics.expiringBonus && (
                <div className="urgent-bonus-card">
                  <h4>{t('claimBonuses.expiringSoon')}</h4>
                  <div className="expiring-bonus">
                    <div className="expiring-name">{analytics.expiringBonus.rule_name}</div>
                    <div className="expiring-date">
                      {t('claimBonuses.expiresDate', { date: formatDate(analytics.expiringBonus.expires_at) })}
                    </div>
                    <div className="expiring-points">+{analytics.expiringBonus.points} {t('claimBonuses.pointsLabel').toLowerCase()}</div>
                  </div>
                </div>
              )}

              {analytics && (
                <div className="bonus-types-breakdown">
                  <h4>{t('claimBonuses.bonusTypes')}</h4>
                  {Object.entries(analytics.bonusByType).map(([type, points]) => (
                    <div key={type} className="type-stat">
                      <span className="type-icon">{getBonusIcon(type)}</span>
                      <span className="type-name">{getBonusTypeLabel(type)}</span>
                      <span className="type-points">+{points}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Center Column - Main Bonuses List */}
          <div className="bonuses-panel-center">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {loading ? (
              <div className="loading-container">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{t('claimBonuses.loading')}</span>
                </div>
                <p>{t('claimBonuses.loading')}</p>
              </div>
            ) : bonuses.length === 0 ? (
              <div className="no-bonuses">
                <div className="no-bonuses-icon">🔍</div>
                <h3>{t('claimBonuses.noBonusesAvailable')}</h3>
                <p>{t('claimBonuses.noBonusesDesc')}</p>
                <button 
                  className="btn btn-primary" 
                  onClick={() => navigate('/')}
                >
                  {t('claimBonuses.returnToDashboard')}
                </button>
              </div>
            ) : (
              <div className="bonuses-list">
                {bonuses.map(bonus => (
                  <div key={bonus.id} className="bonus-card">
                    <div className="bonus-icon">{getBonusIcon(bonus.bonus_type)}</div>
                    <div className="bonus-details">
                      <h3>{bonus.rule_name}</h3>
                      <div className="bonus-type">{getBonusTypeLabel(bonus.bonus_type)}</div>
                      <p className="bonus-description">{bonus.description}</p>
                      <div className="bonus-meta">
                        <div className="bonus-points">+{bonus.points} {t('claimBonuses.pointsLabel').toLowerCase()}</div>
                        <div className="bonus-expiry">
                          {t('claimBonuses.expiresDate', { date: formatDate(bonus.expires_at) })}
                        </div>
                      </div>
                    </div>
                    <div className="bonus-action">
                      <button 
                        className="claim-btn"
                        onClick={() => handleClaimBonus(bonus.id)}
                        disabled={claimingId === bonus.id || claimedIds.includes(bonus.id)}
                      >
                        {claimingId === bonus.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span className="ms-2">{t('claimBonuses.claiming')}</span>
                          </>
                        ) : claimedIds.includes(bonus.id) ? (
                          t('claimBonuses.claimed')
                        ) : (
                          t('claimBonuses.claimNow')
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Earning Tips & Actions */}
          <div className="bonuses-panel-right">
            <div className="tips-card">
              <h3 className="section-title">
                <i className="bi bi-lightbulb"></i>
                {t('claimBonuses.earningTips')}
              </h3>
              
              <div className="tip-item">
                <div className="tip-icon">🎯</div>
                <div className="tip-content">
                  <div className="tip-title">{t('claimBonuses.dailyLoginTip')}</div>
                  <div className="tip-description">{t('claimBonuses.dailyLoginDesc')}</div>
                </div>
              </div>
              <div className="tip-item">
                <div className="tip-icon">👥</div>
                <div className="tip-content">
                  <div className="tip-title">{t('claimBonuses.referFriendsTip')}</div>
                  <div className="tip-description">{t('claimBonuses.referFriendsDesc')}</div>
                </div>
              </div>
              <div className="tip-item">
                <div className="tip-icon">📋</div>
                <div className="tip-content">
                  <div className="tip-title">{t('claimBonuses.completeTasksTip')}</div>
                  <div className="tip-description">{t('claimBonuses.completeTasksDesc')}</div>
                </div>
              </div>
            </div>

            <div className="achievements-card">
              <h3 className="section-title">
                <i className="bi bi-trophy"></i>
                {t('claimBonuses.achievements')}
              </h3>
              
              <div className="achievement-item">
                <div className="achievement-icon">🌟</div>
                <div className="achievement-content">
                  <div className="achievement-title">{t('claimBonuses.bonusHunter')}</div>
                  <div className="achievement-description">{t('claimBonuses.bonusHunterDesc')}</div>
                  <div className="achievement-progress">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: '60%' }}></div>
                    </div>
                    <span className="progress-text">6/10</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </BasePageLayout>
  );
};

export default ClaimBonusesPage;
